import React, {useRef, useState} from 'react';
import TextField, {TextFieldProps} from '@mui/material/TextField';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PaymentIcon from '@mui/icons-material/Payment';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import clsx from 'clsx';
import './ArrTextField.scss';
import {FilledInputProps} from '@mui/material';
import {formatCardNumber} from '../../../utils/formatters';
import {useFieldFocusStyle} from '../../../hooks/utils/useFieldFocusStyle';
import Cancel from '@mui/icons-material/Cancel';
import Search from '@mui/icons-material/Search';

type StateType = 'enabled' | 'error' | 'inactive';
type SupportedTypes = 'text' | 'password' | 'card' | 'search' | 'clear';
type SizeType = 'large' | 'medium' | 'small';

export interface ArrTextFieldProps extends Omit<
  TextFieldProps,
  'variant' | 'type'
> {
  state?: StateType;
  supportText?: string;
  sizeVariant?: SizeType;
  showErrorIcon?: boolean;
  type?: SupportedTypes;
  slotProps?: TextFieldProps['slotProps']; // allow parent to pass slotProps
  onClear?: () => void; // clear handler for search type
  dataTestId: string;
  isAriaDisabled?: boolean;
  readerrorText?: boolean;
}

const ArrTextField: React.FC<ArrTextFieldProps> = ({
  state = 'enabled',
  supportText,
  sizeVariant = 'large',
  showErrorIcon = false,
  placeholder,
  className,
  type = 'text',
  slotProps, // accept here
  onClear,
  dataTestId,
  isAriaDisabled = false,
  readerrorText = false,
  ...rest
}) => {
  const [showPassword, setShowPassword] = useState(false);
  // internal ref to the input element
  const innerInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(
    null
  );
  // parent may pass inputRef via rest; capture it to forward
  const parentInputRef = (rest as any)?.inputRef;

  const setInputRef = (el: HTMLInputElement | HTMLTextAreaElement | null) => {
    innerInputRef.current = el;
    try {
      if (typeof parentInputRef === 'function') parentInputRef(el);
      else if (parentInputRef && typeof parentInputRef === 'object')
        parentInputRef.current = el;
    } catch {
      // ignore
    }
  };

  const isError = state === 'error';
  const isInactive = state === 'inactive';

  const {borderClass, focusHandler, mouseKeyDownHandler} = useFieldFocusStyle({
    tabFocusClass: 'tabFocusedBorder',
    mouseFocusClass: 'mouseFocusedBorder',
  });

  const onFocus = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    focusHandler?.(e);
    rest.onFocus?.(e);
  };

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    mouseKeyDownHandler?.(e);
    rest.onMouseDown?.(e);
  };

  // password visibility toggle handler
  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  // Custom onChange handler (with card formatting support & max length)
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isAriaDisabled) {
      return;
    }
    if (type === 'card') {
      // keep only digits
      let digitsOnly = e.target.value.replace(/\D/g, '');

      // restrict to max 16 digits
      if (digitsOnly.length > 16) {
        digitsOnly = digitsOnly.slice(0, 16);
      }

      // apply formatting
      const formattedValue = formatCardNumber(digitsOnly);

      // set back the formatted value
      e.target.value = formattedValue;
    }

    rest.onChange?.(e); // forward event to parent
  };

  const inputId = rest.id || `arr-textfield-${rest.name}`;
  const helperTextId = supportText ? `${inputId}-helper-text` : undefined;
  const errorTextId = isError ? `${inputId}-error-text` : undefined;

  const describedBy =
    [supportText ? helperTextId : null, isError ? errorTextId : null]
      .filter(Boolean)
      .join(' ') || undefined;
  const triggerClear = () => {
    if (onClear) {
      onClear();
      return;
    }
    if (!rest.onChange) return;

    const event = {
      target: {
        value: '',
        name: rest.name,
      },
    } as React.ChangeEvent<HTMLInputElement>;

    rest.onChange(event);
  };

  const hasValue = Boolean(rest?.value);
  const showSearchType = type === 'search' && !isError && !showErrorIcon;
  const showClearButton =
    (type === 'search' && hasValue && !isError && !showErrorIcon) ||
    (type === 'clear' && hasValue);

  return (
    <div
      className={clsx('arr-textfield-wrapper', className)}
      data-testid={`arr-text-field-div-${dataTestId}`}
    >
      <TextField
        {...rest}
        id={inputId}
        type={type === 'password' && !showPassword ? 'password' : 'text'}
        variant="filled"
        placeholder={placeholder}
        disabled={!isAriaDisabled && isInactive}
        error={isError}
        aria-invalid={isError}
        aria-disabled={isAriaDisabled}
        aria-describedby={describedBy}
        onChange={handleChange}
        label={rest.label}
        data-testid={`${dataTestId}-wrapper`}
        inputRef={setInputRef}
        slotProps={{
          ...slotProps, // merge with parent props
          htmlInput: {
            ...(slotProps?.htmlInput as Record<string, unknown>),
            'data-testid': dataTestId,
          },
          input: {
            disableUnderline: true,
            readOnly: isAriaDisabled,
            style: {
              caretColor: isAriaDisabled ? 'transparent' : 'auto',
            },
            ...slotProps?.input,
            endAdornment: (
              <>
                {/* parent provided adornment */}
                {(slotProps?.input as Partial<FilledInputProps>)?.endAdornment}

                {/* Error Icon (decorative) */}
                {isError && showErrorIcon && (
                  <ErrorOutlineIcon
                    aria-hidden="true"
                    className="arr-textfield-error-icon"
                  />
                )}

                {/* Password Toggle Icon */}
                {type === 'password' && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={togglePasswordVisibility}
                      edge="end"
                      aria-label={
                        showPassword ? 'Hide password' : 'Show password'
                      }
                      aria-pressed={showPassword}
                      data-testid={`text-field-toggle-password-icon-button-${inputId}`}
                    >
                      {showPassword ? (
                        <VisibilityOff
                          aria-hidden="true"
                          className="arr-textfield-icon-color"
                        />
                      ) : (
                        <Visibility
                          aria-hidden="true"
                          className="arr-textfield-icon-color"
                        />
                      )}
                    </IconButton>
                  </InputAdornment>
                )}

                {/* Payment Card Icon (decorative) */}
                {type === 'card' && (
                  <InputAdornment position="end">
                    <PaymentIcon
                      aria-hidden="true"
                      className="arr-textfield-icon-color"
                    />
                  </InputAdornment>
                )}

                {(showSearchType || showClearButton) && (
                  <InputAdornment position="end">
                    {showClearButton ? (
                      <IconButton
                        onClick={triggerClear}
                        aria-label="Clear search"
                        edge="end"
                        size="small"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            triggerClear();
                            e.preventDefault();
                            e.stopPropagation();
                            innerInputRef.current?.focus(); //  move focus back to input
                          }
                        }}
                        className="clear-icon-btn"
                        data-testid={`${dataTestId}-clear-button`}
                      >
                        <Cancel
                          className="clear-btn"
                          fontSize={
                            sizeVariant === 'large' ? 'medium' : 'small'
                          }
                        />
                      </IconButton>
                    ) : (
                      <Search
                        aria-hidden="true"
                        data-testid={`${dataTestId}-search-icon`}
                        className={clsx('arr-textfield-icon-color', {
                          'arr-textfield-icon-color--inactive':
                            isInactive || isAriaDisabled,
                        })}
                        fontSize={sizeVariant === 'large' ? 'medium' : 'small'}
                      />
                    )}
                  </InputAdornment>
                )}
              </>
            ),
          } as Partial<FilledInputProps>,
          formHelperText: {
            id: helperTextId,
            className: 'arr-textfield-helper',
            ...slotProps?.formHelperText,
          },
        }}
        className={clsx(
          'arr-textfield',
          {
            'arr-textfield--error': isError,
            'arr-textfield--inactive': isInactive || isAriaDisabled,
            'arr-textfield--enabled': state === 'enabled',
            'arr-textfield--show-password': type === 'password' && showPassword,
          },
          `arr-textfield--${sizeVariant}`,
          borderClass
        )}
        helperText={
          isError ? (
            <span
              id={errorTextId}
              role="alert"
              aria-live="assertive"
              aria-label={readerrorText ? supportText : undefined}
              aria-atomic="true"
              data-testid={`arr-error-text-${dataTestId}`}
            >
              {supportText}
            </span>
          ) : (
            supportText
          )
        }
        onFocus={onFocus}
        onMouseDown={onMouseDown}
      />
    </div>
  );
};

export default ArrTextField;
